import { CircularProgress, Grid, LinearProgress, Link, FormControlLabel } from "@mui/material";
import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import DetailsPanel from "components/Elements/DetailsPanel";
import {
	isoDateWithoutTimeZone,
	debounce,
	MuiFormatDate,
	getLocalStorageData,
	defaultPageSize,
	customFromattedDate,
	checkIsDefectPending,
	createTableSortHandler,
} from "helpers/utils";
import { useDispatch } from "react-redux";
import { showError } from "../../../redux/common/actions";
import Header from "./Header";
import { makeStyles } from "tss-react/mui";

import SafteryCritical from "assets/icons/safety-critical.svg";
import DefectListTable from "./DefectsListTable";
import { appPath, defectsPath } from "helpers/routePaths";

import ColourConstants from "helpers/colourConstants";
import CustomDateRange from "./CustomDateRange";
import { useUserSearch } from "hooks/useUserSearch";
import DeleteDialog from "components/Elements/DeleteDialog";
import { changeDate } from "helpers/date";
import {
	getCountOfDefectsList,
	getDefectAutocompleteSearch,
	getDefectsList,
} from "services/defects/defectsList";
import { DefectTableColumns, DefectTableHeader } from "constants/DefectDetails";
import TabTitle from "components/Elements/TabTitle";
import mainAccess from "helpers/access";
import AutoCompleteBox from "components/Elements/EMIChip/AutoCompleteBox";
import HtmlTooltip from "components/Elements/HtmlTooltip";
import ErrorMessageWithErrorIcon from "components/Elements/ErrorMessageWithErrorIcon";
import WarningMessageWithErrorIcon from "components/Elements/WarningMessageWithErrorIcon";
import { getIntegrationInstances } from "services/integrations";
import useChips from "hooks/useChips";
import { ChipTypeEnums } from "constants/enums";
import { FilterProvider, FilterContainer, useFilters } from "components/Elements/Filters";
import { useFilterInitialization } from "components/Elements/Filters/Dropdown/hooks/useFilterInitialization";
import { useDefectStatus } from "components/Elements/Filters/Dropdown/hooks/useDefectStatus";

const useStyles = makeStyles()((theme) => ({
	loading: {
		position: "absolute",
		width: "100%",
		left: 0,
		top: 0,
		right: 0,
	},
}));

const defaultCustomDate = { from: "", to: "" };

// Filter configuration for the new Filter framework
const createFilterConfig = (
	hasIntegrationInstances = false,
	showFunctionalLocationOnly = false
) => {
	const enableQuickFilter = hasIntegrationInstances || showFunctionalLocationOnly;

	return {
		filters: {
			defectStatus: true,
			timeframe: {
			allowCustomRange: true,
			allowedRanges: ['today', 'yesterday', 'this-week', 'last-week', 'last-month', 'last-year', 'last-7-days', 'last-14-days', 'last-30-days', 'last-90-days', 'custom']
		},
			department: { multiSelect: true },
			quickFilter: {
				enabled: enableQuickFilter,
				multiSelect: true,
				persistAll: true,
				hasIntegrationInstances,
				showFunctionalLocationOnly,
			},
			tableSort: true,
			chips: {
				enabled: true,
				hiddenTypes: [
					ChipTypeEnums.SiteDepartmentID,
					ChipTypeEnums.DefectStatusID,
					ChipTypeEnums.DefectStatusType,
				], // Hide department and defect status chips
			},
		},
	};
};

// Helper function to extract valid department IDs from department value
// Filters out empty, null, and 'all' departments
const getValidDepartmentIds = (departmentValue) => {
	if (!departmentValue) return [];
	const depts = Array.isArray(departmentValue) ? departmentValue : [departmentValue];
	return depts
		.filter(d => d?.id && d.id !== '' && d.id !== 'all')
		.map(d => d.id);
};

function DefectsListsContent({
	statusFromMemory,
	departmentFromMemory,
	timeFrameFromMemory,
	defectIntegrationInstance,
}) {
	const { filterValues, allFiltersInitialized, updateFilter, initialValues, cleanupAssetChips } = useFilters();

	// Get all defect statuses to expand group statuses into individual status IDs
	const allDefectStatuses = useDefectStatus(false);

	// Clean up any stale asset chips when component mounts
	useEffect(() => {
		// Check if localStorage is available (it may not be in test environments)
		if (!localStorage || typeof localStorage.getItem !== 'function') {
			// Just clean up asset chips from filter values
			cleanupAssetChips();
			return;
		}

		// Check if there's any temp asset chip data
		const tempData = localStorage.getItem('temp_asset_drilldown');
		if (tempData) {
			try {
				// If the data exists, mark it as used
				const parsedData = JSON.parse(tempData);
				parsedData.isUsed = true;
				
				// Only call setItem if it's available
				if (typeof localStorage.setItem === 'function') {
					localStorage.setItem('temp_asset_drilldown', JSON.stringify(parsedData));
				}
				
				// The cleanup will happen via the timeout in navigateWithFilters
			} catch (error) {
				// If there's an error parsing, remove it - but only if removeItem is available
				if (typeof localStorage.removeItem === 'function') {
					localStorage.removeItem('temp_asset_drilldown');
				}
			}
		}
		
		// Clean up asset chips from filter values
		cleanupAssetChips();
	}, [cleanupAssetChips]);
	
	// Helper to get current filter values for API calls
	const getCurrentFilterValues = () => {
		const timeframeValue = filterValues['timeframe']?.value;
		const tableSortParams = filterValues['tableSort']?.params;
		const quickFilterParams = filterValues['quickFilter']?.params;

		return {
			defectStatusID: filterValues['defectStatus']?.params?.defectStatusID || '',
			defectStatusType: filterValues['defectStatus']?.params?.defectStatusType || '',
			fromDate: timeframeValue?.fromDate || '',
			toDate: timeframeValue?.toDate || '',
			sortField: tableSortParams?.sortField || '',
			sortOrder: tableSortParams?.sortOrder || 'asc',
			showSAPErrorsOnly: quickFilterParams?.showSAPErrorsOnly || false,
			showFunctionalLocationOnly: quickFilterParams?.showFunctionalLocationOnly || false,
		};
	};
	
	// Initialize chips using the generic useFilterInitialization hook
	useFilterInitialization('chips', [], {
		getDefaultValue: () => [],
		getParams: (chips) => ({ chips }),
		waitForData: false, // Don't wait for dataSource since chips start empty
	});


	// Removed updateChipsFromFilters function - logic moved inline to prevent double API calls
	// init hooks
	const dispatch = useDispatch();
	const { classes, cx } = useStyles();
	const { allData, setAllData, searchedData, searchQuery, setSearchData } =
		useUserSearch();
	// Get chips from FilterContext instead of useChips
	const chips = filterValues.chips?.value || [];

	// Memoized calculation of updated chips for both API calls and Header
	// Only recalculates when filter values change
	const updatedChips = useMemo(() => {
		const currentChips = filterValues.chips?.value || [];
		const hiddenTypes = [ChipTypeEnums.SiteDepartmentID, ChipTypeEnums.DefectStatusID, ChipTypeEnums.DefectStatusType];

		// Preserve all non-hidden chips (user-added chips from autocomplete, URL, etc.)
		const preservedChips = currentChips.filter(chip => !hiddenTypes.includes(chip.chipType));

		// Add department chips using the helper function
		const departmentValue = filterValues.department?.value;
		const validDeptIds = getValidDepartmentIds(departmentValue);
		const departmentChips = validDeptIds.map(id => ({
			chipType: ChipTypeEnums.SiteDepartmentID,
			id,
			value: "",
		}));

		// Add defect status chip if defectStatus filter has value
		const statusParams = filterValues.defectStatus?.params;
		const statusChips = [];
		if (statusParams?.defectStatusType && statusParams.defectStatusType !== "") {
			// Group status (All Complete/All Outstanding)
			// Expand to all individual status IDs of that type
			const statusesOfType = allDefectStatuses.filter(
				status => status.type === statusParams.defectStatusType &&
				status.id !== "C" &&
				status.id !== "O" &&
				status.id !== ""
			);
			statusesOfType.forEach(status => {
				statusChips.push({
					chipType: ChipTypeEnums.DefectStatusID,
					id: status.id,
					value: "",
				});
			});
		} else if (statusParams?.defectStatusID && statusParams.defectStatusID !== "") {
			// Individual status
			statusChips.push({
				chipType: ChipTypeEnums.DefectStatusID,
				id: statusParams.defectStatusID,
				value: "",
			});
		}

		return [...preservedChips, ...departmentChips, ...statusChips];
	}, [filterValues.chips?.value, filterValues.department?.value, filterValues.defectStatus?.params, allDefectStatuses]);
	
	const {
		isDropDownChanging,
		isRemovingChip,
		setDropDownChange,
		dataForFetchingDefect,
		setDataForFetchingDefect,
		autocompleteData,
		setAutocompleteData,
	} = useChips();

	const tableRef = useRef();

	// Handle autocomplete selection - integrate with Filter Framework
	const handleAutoCompleteContent = async (item) => {
		setDropDownChange(false);
		
		// Add the selected item as a chip to the Filter Framework
		const currentChips = filterValues.chips?.value || [];
		const isDuplicate = currentChips.some(
			(existingItem) =>
				existingItem.id === item.id &&
				existingItem.chipType === item.chipType &&
				existingItem.value === item.value
		);
		
		if (!isDuplicate) {
			const updatedChips = [...currentChips, item];
			updateFilter('chips', updatedChips, { chips: updatedChips });
		}
		
		setAutocompleteData([]);
		setDataForFetchingDefect({
			pageNumber: 1,
			pageSize: defaultPageSize(),
			search: "",
			sortField: "",
			sort: "",
		});
	};
	// gets localstoarge || sessionStorage data
	const {
		customCaptions,
		position,
		siteAppID,
		siteID,
		application,
		site: { siteDepartmentID, siteDepartmentName },
		weekStartDay,
	} = getLocalStorageData("me");
	// Legacy analytics data handling - now handled by Filter Framework URL parameters
	// This section can be removed as filters are automatically initialized from URL params

	// Get current table sort from FilterFramework (convert to array format for table component)
	const tableSortParams = filterValues.tableSort?.params || { sortField: "", sortOrder: "asc" };
	const currentTableSort = [tableSortParams.sortField, tableSortParams.sortOrder];
	const [countOFDefects, setCountOfDefect] = useState(0);
	const [loading, setLoading] = useState(true);
	const [isSearching, setSearching] = useState(false);
	const [deleteID, setDeleteID] = useState(null);
	const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
	const [customDate, setCustomDate] = useState(
		timeFrameFromMemory !== null && timeFrameFromMemory.id === 7
			? {
					from: MuiFormatDate(timeFrameFromMemory.fromDate),
					to: MuiFormatDate(timeFrameFromMemory.toDate),
			  }
			: defaultCustomDate
	);
	const [openCustomDatePopup, setOpenCustomDatePopup] = useState(false);
	const [isCustomDateRangeError, setCustomDateRangeError] = useState(false);
	// for formatted data

	const formattedData = (data, navigate) => {
		return data.map((x) => ({
			...x,
			modelName: x.modelModel ? x.modelName + " " + x.modelModel : x.modelName,
			taskName: x?.actionName ? x?.actionName + " " + x?.taskName : x?.taskName,
			defectStatusName: (
				<span
					style={{
						color: x?.defectStatusType === "C" ? ColourConstants.green : "",
					}}
				>
					{x?.defectStatusName}
				</span>
			),
			riskRatingName: (
				<span
					style={{
						color: x?.safetyCritical === true ? ColourConstants.red : "",
					}}
				>
					{x?.riskRatingName}
				</span>
			),
			number: (
				<>
					<Link
						onClick={() => navigate(`${appPath}${defectsPath}/${x.id}`)}
						href={`${appPath}${defectsPath}/${x.id}`}
						style={{
							color: ColourConstants.activeLink,
							cursor: "pointer",
							textDecoration: "none",
						}}
					>
						{x.number}
					</Link>
					&nbsp;
					{x?.sapError && <ErrorMessageWithErrorIcon message={x?.sapError} />}
					{!x?.exported &&
					checkIsDefectPending(x, defectIntegrationInstance) ? (
						<WarningMessageWithErrorIcon
							message={`Defect not synchronized to ${defectIntegrationInstance?.name}`}
						/>
					) : null}
				</>
			),
			safetyCritical:
				x.safetyCritical === true ? (
					<img src={SafteryCritical} alt="saftey critical" />
				) : (
					""
				),

			createdDateTime: isoDateWithoutTimeZone(x.createdDateTime + "Z"),
			details: (
				<HtmlTooltip title={x.details}>
					<p className="max-two-line">
						<Link
							onClick={() => navigate(`${appPath}${defectsPath}/${x.id}`)}
							href={`${appPath}${defectsPath}/${x.id}`}
							style={{
								color: ColourConstants.commonText,
								cursor: "pointer",
								textDecoration: "none",
							}}
						>
							{x.details}
						</Link>
					</p>
				</HtmlTooltip>
			),
		}));
	};

	// Removed separate chip updating useEffect to prevent double API calls
	// Chip updating is now integrated into the main filter change handler
	
	// Effect to handle filter changes and fetch data
	useEffect(() => {
		if (!allFiltersInitialized) {
			return;
		}
		
		const handleFilterChange = async () => {
			// Reset to page 1 when filters change
			setDataForFetchingDefect(prev => ({
				...prev,
				pageNumber: 1
			}));
						
			setSearching(true);
			setDropDownChange(true);
			
			// Clear both data and count to prevent infinite scroll from using stale values
			setAllData([]);
			setCountOfDefect(0);
			
			const params = getCurrentFilterValues();
			
			// Fetch with current filter values and calculated chips
			await fetchDefectList({
				chips: updatedChips,
				search: searchRef.current,
				...params,
				forcePageNumber: 1, // Force page 1 for filter changes
			});
			setSearching(false);
			setDropDownChange(false);
		};

		handleFilterChange();
	}, [filterValues, allFiltersInitialized]);

	const searchRef = useRef("");
	const apiRef = useRef({});


	// handling Custom Date input change
	const handleCustomDateChange = (type, e) => {
		setCustomDate({ ...customDate, [type]: e.target.value });
	};

	// close Custom Date popup
	const handleCloseCustomDate = () => {
		setOpenCustomDatePopup(false);
		setCustomDateRangeError(defaultCustomDate);
	};

	// when the Custom Date popup is submitted
	const handleCustomDateSubmit = async (e) => {
		e.preventDefault();

		const isFromDateEmpty = customDate.from === "";
		const isToDateEmpty = customDate.to === "";
		// show error if date fields are empty
		if (isFromDateEmpty || isToDateEmpty) {
			setCustomDateRangeError({ from: isFromDateEmpty, to: isToDateEmpty });
			return;
		}

		setSearching(true);

		const formattedCustomDate = customFromattedDate(customDate);

		// Note: The Filter framework will handle the date range state
		// This function is kept for compatibility with the CustomDateRange component
		const currentFilters = getCurrentFilterValues();
		await fetchDefectList({
			chips,
			search: searchRef.current,
			...currentFilters,
			fromDate: formattedCustomDate.fromDate,
			toDate: formattedCustomDate.toDate,
		});
		setSearching(false);

		handleCloseCustomDate();
	};
	//
	// calling defect list api after mounting
	const fetchDataFromEffect = async () => {
		setSearching(false);
		setLoading(false);
	};

	// searching for services
	const handleSearch = debounce(async (value) => {
		setSearching(true);
		searchRef.current = value;
		const autoCompleteRes = await getDefectAutocompleteSearch({
			search: value,
		});

		setAutocompleteData(autoCompleteRes?.data);
		if (!value || value === "")
			setDataForFetchingDefect({
				pageNumber: 1,
				pageSize: defaultPageSize(),
				search: "",
				sortField: "",
				sort: "",
			});
		setSearching(false);
	}, 500);


	// opens DELETE defect  popup
	const handleDeleteDialogOpen = (id) => {
		setDeleteID(id);
		setOpenDeleteDialog(true);
	};
	//for fetching the chips
	useEffect(() => {
		if (dataForFetchingDefect?.search.length >= 3) {
			handleSearch(dataForFetchingDefect?.search);
		} else {
			setAutocompleteData([]);
		}
	}, [dataForFetchingDefect?.search]);

	// remove defect from client table list after successfull deletion of defect
	const handleRemoveData = (id) => {
		setAllData([...allData.filter((d) => d.id !== id)]);
		setCountOfDefect((prev) => prev - 1);
	};

	// attemp to fetch defects list
	const fetchDefectList = useCallback(
		async ({
			chips = "",
			defectStatusID = "",
			defectStatusType = "",
			siteDepartmentID = "",
			fromDate = "",
			toDate = "",
			sortField = "",
			sortOrder = "",
			shouldCount = true,
			search = "",
			forcePageNumber = null,
			showSAPErrorsOnly = false,
			showFunctionalLocationOnly = false,
		}) => {
			try {
				const response = await Promise.all([
					getDefectsList({
						siteAppId: siteAppID,
						chips,
						siteDepartmentID,
						fromDate,
						toDate,
						sortField,
						sortOrder,
						pageNumber: forcePageNumber || dataForFetchingDefect.pageNumber,
						pageSize: dataForFetchingDefect.pageSize,
						search,
						onlySAPErrors: showSAPErrorsOnly,
						onlyFLOC: showFunctionalLocationOnly,
					}),
					shouldCount &&
						getCountOfDefectsList({
							siteAppId: siteAppID,
							search,
							fromDate,
							toDate,
							chips,
							onlySAPErrors: showSAPErrorsOnly,
							onlyFLOC: showFunctionalLocationOnly,
						}),
				]);

				if (response[0].status) {
					setAllData(response[0].data);
					response[1].status && setCountOfDefect(response[1].data);
				} else {
					dispatch(
						showError(
							response?.data?.detail ||
								`Failed to fetch ${customCaptions?.defect} list`
						)
					);
				}
			} catch (error) {
				dispatch(
					showError(
						error?.response?.detail ||
							`Failed to fetch ${customCaptions?.defect} list`
					)
				);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			siteAppID,
			dispatch,
			setAllData,
			searchRef.current,
			dataForFetchingDefect.pageNumber,
		]
	);
	const mainData = searchQuery.length === 0 ? allData : allData;

	// Analytics redirection is now handled automatically by Filter Framework URL parameter initialization
	// No additional logic needed - filters are automatically populated from URL parameters

	// fetch initial data after mounting (excluding defect list - handled by filters)
	useEffect(() => {
		document.body.style.overflowX = "hidden";
		document.body.style.maxWidth = "100%";
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [siteID]);

	useEffect(() => {
		if (siteID) {
			fetchDataFromEffect();
		}
	}, [siteID]);

	// Memoized department IDs for consistent data type to DefectListTable
	// Always returns an array format
	const departmentIds = useMemo(() => {
		return getValidDepartmentIds(filterValues['department']?.value);
	}, [filterValues['department']?.value]);

	// Create a memoized sort handler to prevent duplicate calls using the helper function
	const handleTableSort = useCallback((sortField, sortOrder) => {
		setSearching(true);
		createTableSortHandler(tableSortParams, updateFilter)(sortField, sortOrder);
		// We don't need to fetch here as the filter change will trigger the useEffect
		setSearching(false);
	}, [tableSortParams, updateFilter]);

	if (loading) return <CircularProgress />;

	return (
		<>
			<TabTitle
				title={`${customCaptions.defectPlural} | ${application.name}`}
			/>
			{(isSearching || isRemovingChip) && (
				<LinearProgress className={classes.loading} />
			)}

			<DeleteDialog
				entityName={customCaptions?.defect}
				open={openDeleteDialog}
				closeHandler={() => setOpenDeleteDialog(false)}
				deleteID={deleteID}
				deleteEndpoint={"/api/defects"}
				handleRemoveData={handleRemoveData}
			/>
			<CustomDateRange
				open={openCustomDatePopup}
				handleChange={handleCustomDateChange}
				customDate={customDate}
				closeHandler={handleCloseCustomDate}
				onSubmit={handleCustomDateSubmit}
				isError={isCustomDateRangeError}
				isLoading={isSearching}
			/>
			<div className="container">
				<Header
					dataLength={countOFDefects}
					defectsCC={customCaptions?.defectPlural}
					customCaptions={customCaptions}
					fromDate={filterValues['timeframe']?.value?.fromDate || ""}
					toDate={filterValues['timeframe']?.value?.toDate || ""}
					chips={updatedChips}
					page={dataForFetchingDefect.pageNumber}
					currentTableSort={currentTableSort}
					pageSize={defaultPageSize()}
					siteAppId={siteAppID}
					showSAPErrorsOnly={filterValues['quickFilter']?.params?.showSAPErrorsOnly || false}
					showFunctionalLocationOnly={filterValues['quickFilter']?.params?.showFunctionalLocationOnly || false}
				/>
				<div
					className="detailsContainer"
					style={{ alignItems: "center", marginTop: "-15px" }}
				>
					<DetailsPanel
						showHeader={false}
						description={`View all ${customCaptions?.defectPlural} across your operations`}
					/>
					<AutoCompleteBox
						searchQuery={dataForFetchingDefect?.search}
						setSearchQuery={(e) => {
							e.persist();
							setDataForFetchingDefect((prev) => ({
								...prev,
								search: e.target.value,
							}));
						}}
						width="100%"
						required
						label="Services Access"
						setSelectedValue={handleAutoCompleteContent}
						selectdValueToshow="name"
						dataSource={autocompleteData}
						isSearching={isSearching}
						chips={chips}
					/>
				</div>


				{/* Filter Framework with ChipsFilter */}
				<div style={{ marginTop: 20 }}>
					<FilterContainer />
				</div>

				<div className="dynamic-width-table">
					<DefectListTable
						ref={tableRef}
						data={mainData}
						headers={DefectTableHeader(customCaptions)}
						columns={DefectTableColumns()}
						setData={setAllData}
						handleSort={handleTableSort}
						searchQuery={searchRef.current}
						formattedData={formattedData}
						searchedData={searchedData}
						setSearchData={setSearchData}
						handleDeleteDialogOpen={handleDeleteDialogOpen}
						searchText={searchRef.current}
						page={dataForFetchingDefect.pageNumber}
						countOFDefects={countOFDefects}
						department={JSON.stringify(departmentIds)}
						status={filterValues['defectStatus']?.value?.id}
						date={filterValues['timeframe']?.value}
						setDataForFetchingDefect={setDataForFetchingDefect}
						currentTableSort={currentTableSort}
						siteAppID={siteAppID}
						content={customCaptions?.defectPlural}
						isReadOnly={position?.[mainAccess.defectAccess] === "R"}
						chips={updatedChips}
					/>
				</div>
			</div>
		</>
	);
}

// Wrapper component that provides FilterContext
function DefectsLists(props) {
	const [hasIntegrationInstances, setHasIntegrationInstances] = useState(false);
	const [defectIntegrationInstance, setDefectIntegrationInstance] = useState(null);
	const { application } = getLocalStorageData("me");
	const showAssetReferences = application?.showAssetReferences || false;

	// Fetch integration instances to determine if Quick Filter should show SAP Error option
	useEffect(() => {
		const fetchIntegrationInstances = async () => {
			try {
				const response = await getIntegrationInstances();
				if (response?.status && response?.data?.length) {
					// Filter for active SAP-Hana-Cloud integration instances
					const sapHanaInstances = response.data.filter(
						instance => instance.integrationType === "SAP-Hana-Cloud" && instance.isActive
					);

					if (sapHanaInstances.length > 0) {
						const formattedIntegrationInstance = {
							...sapHanaInstances[0],
							fieldMappings: sapHanaInstances[0]?.fieldMappings
								? JSON.parse(sapHanaInstances[0].fieldMappings) ?? {}
								: [],
							settings: sapHanaInstances[0]?.settings
								? JSON.parse(sapHanaInstances[0].settings) ?? {}
								: [],
						};
						setDefectIntegrationInstance(formattedIntegrationInstance);
						setHasIntegrationInstances(true);
					} else {
						setHasIntegrationInstances(false);
					}
				} else {
					setHasIntegrationInstances(false);
				}
			} catch (error) {
				console.error("Error fetching integration instances:", error);
				setHasIntegrationInstances(false);
			}
		};

		fetchIntegrationInstances();
	}, []);

	// Create filter config with current integration and asset reference settings
	const filterConfig = useMemo(
		() => createFilterConfig(hasIntegrationInstances, showAssetReferences),
		[hasIntegrationInstances, showAssetReferences]
	);

	return (
		<FilterProvider
			config={filterConfig}
			storageKey="defects"
		>
			<DefectsListsContent {...props} defectIntegrationInstance={defectIntegrationInstance} />
		</FilterProvider>
	);
}

export default DefectsLists;
